import { FC, useEffect, useMemo, useState } from 'react';
import V2FormCheckbox from '@/common/components/Form/V2FormCheckbox/V2FormCheckbox';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { OTHER_TYPE_ID, OTHER_TYPE_INPUT_TEXT, OTHER_TYPE_NAME, setComValue } from '../config';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import { useDispatch, useSelector } from 'react-redux';
import { changeDynamicOtherFormNameArr } from '@/store/common';
import styles from './index.module.less';

interface InputInfoI {
  visible:boolean;// 是否展示
  value:string;// input表单值
}

/**
 * @Description 复选组件
 */
const Checkbox: FC<{
  propertyItem: any;
  optLabelFieldName?: string;
  optValueFieldName?: string;
  disabled?: boolean;
  required?: boolean;
  /** 单选、多选、输入框等值变更时的提示内容 */
  showHintStr?: any;
  form: any;
}> = ({
  propertyItem,
  optLabelFieldName = 'name',
  optValueFieldName = 'id',
  disabled,
  required,
  showHintStr,
  form,
}) => {
  const dispatch = useDispatch();
  const dynamicOtherFormNameArr = useSelector((state: any) => state.common.dynamicOtherFormNameArr); // 存放“其他”表单的formName

  const [helpInfo, setHelpInfo] = useState<string>('');
  const [selectValue, setSelectValue] = useState<any>(null);// 保存radio值
  const [inputInfo, setInputInfo] = useState<InputInfoI>({
    visible: false,
    value: ''
  });

  const label = propertyItem.anotherName || propertyItem.name || '';

  const restriction = propertyItem.restriction ? JSON.parse(propertyItem.restriction) : {};
  const placeholder = restriction.placeholder ? restriction.placeholder : `请选择${label || ''}`;
  const selectMin = restriction.selectMin;
  const selectMax = restriction.selectMax;
  /**
   * 子表单格式需要支持 identification 传入数组形式，如 ['subForm', 0, 'radio']
   * 因为子表单内的数组形式暂时不考虑 customEstimateSalesKWK[identification]，如果需要，后续可以判断转为字符串
   */
  const identification = propertyItem.identification;
  /** 如果 identification 是数组格式，那么“其他”输入框的 prop 也是数组，将数组最后一项拼接 _otherTypeInputText，如 ['subForm', 0, 'radio' + OTHER_TYPE_INPUT_TEXT] */
  const otherTypeInputTextName = Array.isArray(identification)
    ? identification.slice(0, -1).concat(identification[identification.length - 1] + OTHER_TYPE_INPUT_TEXT)
    : `${identification}${OTHER_TYPE_INPUT_TEXT}`;

  const options = useMemo(() => {
    if (!propertyItem) return;
    const _options = Array.isArray(propertyItem.propertyConfigOptionVOList) ? propertyItem.propertyConfigOptionVOList : [];

    // 根据restriction中有withOther判断是否存在“其他”选项，
    if (restriction?.withOther && !_options.map((item) => item.id).includes(OTHER_TYPE_ID)) {
      _options.push({
        name: OTHER_TYPE_NAME,
        id: OTHER_TYPE_ID,
      });
      dispatch(changeDynamicOtherFormNameArr(Array.from(new Set([...dynamicOtherFormNameArr, otherTypeInputTextName]))));
    }
    return _options;
  }, []);

  // 回显
  useEffect(() => {
    // 使用更通用的方式获取选中项和“其他”
    const selectedId = form.getFieldValue(identification);
    const otherTypeInputText = form.getFieldValue(otherTypeInputTextName);
    if (Array.isArray(selectedId)) {
      changeHandle(selectedId);
      form.setFieldValue(otherTypeInputTextName, otherTypeInputText);
    }
  }, [propertyItem]);

  const rules = [
    { required, message: placeholder },
    () => ({
      validator(_, value) {
        if (+selectMin && isNotEmptyAny(value) && value.length < selectMin) {
          return Promise.reject(`请至少选择${selectMin}项`);
        }
        if (+selectMax && isNotEmptyAny(value) && value.length > selectMax) {
          return Promise.reject(`最多可选择${selectMax}项`);
        }
        return Promise.resolve();
      },
    }),
  ];

  const changeHandle = (val: number[]) => {
    setSelectValue(val);

    // 是否选中了“其他”
    if (val.includes(OTHER_TYPE_ID)) {
      setInputInfo({
        visible: true,
        value: ''
      });
    } else {
      setInputInfo({
        visible: false,
        value: ''
      });
    }

    const { restriction } = propertyItem || {};
    setHelpInfo(''); // 清空
    // 取消选中或者没有配置时不继续执行
    if (!restriction) return;
    const parsedRestriction = JSON.parse(restriction);
    const { redMark } = parsedRestriction || {};
    if (!(isArray(redMark) && isArray(val))) return;
    const str: string[] = [];
    val.forEach((item: number) => {
      redMark.forEach((redMarkItem: any) => {
        const { optionId, isRedMark, isItemTip, itemTip, isPageTip } = redMarkItem;
        if (optionId !== item) return;
        isRedMark && isItemTip && itemTip && (str.push(itemTip));
        isPageTip && showHintStr && (showHintStr());
      });
    });
    setHelpInfo(str.join('、'));

  };

  useEffect(() => {
    if (inputInfo.visible && inputInfo.value) {
      propertyItem[OTHER_TYPE_INPUT_TEXT] = inputInfo.value;
      setComValue(propertyItem, selectValue);
    }
  }, [inputInfo.value, selectValue]);

  return (
    <>
      <V2FormCheckbox
        className={styles.checkbox}
        label={label}
        name={identification}
        options={options}
        fieldNames={{
          label: optLabelFieldName,
          value: optValueFieldName
        }}
        disabled={disabled}
        required={required}
        rules={rules}
        onChange={changeHandle}
        formItemConfig={{
          help: helpInfo ? <div className='c-f23'>{helpInfo}</div> : null,
        }}
      />
      {!!inputInfo.visible && <V2FormInput
        className={styles.checkboxOther}
        name={otherTypeInputTextName}
        maxLength={50}
        onChange={(e) => setInputInfo((state) => ({
          ...state,
          value: e.target.value
        }))}
        placeholder='请输入其他选项值'
        rules={[{ required: true, message: '请输入其他选项值' }]}
      />}
    </>
  );
};

export default Checkbox;

